import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Button,
    FormControlLabel,
    makeStyles,
    Popover,
    Radio,
    RadioGroup,
    Theme,
    Typography,
} from '@material-ui/core'
import { useHoveredItemActions } from 'components/WorkspaceEditor/HoveredItemStore'
import React, { memo, useCallback } from 'react'

/**
 * Extends the MenuItemConfig interface to include additional properties
 * for managing the state and appearance of a menu item.
 */
export interface CompleteMenuItemConfig extends MenuItemConfig {
    /** Optional flag indicating whether the menu item is disabled. */
    isDisabled?: boolean
    /** Optional flag indicating whether the menu item is hidden. */
    isHidden?: boolean
    /** Optional small label text for the menu item. */
    smallLabel?: string
}

/** Props for the ManageOptionsPopover component. */
export interface ManageOptionsPopoverProps {
    /** The element to which the popover is anchored. */
    anchorElement: Element | null
    /** Indicates whether the popover is open. */
    isOpen: boolean
    /**
     * Callback function to handle the cancel action.
     * @param e - The event object.
     */
    onClose: (e: any) => void
    /**
     * Callback function to handle the save action.
     * @param e - The event object.
     */
    onSave: (e: any) => void
}

const useStyles = makeStyles((theme: Theme) => ({
    menuText: {
        fontSize: pxToRem(12),
        color: '#211F20',
        cursor: 'pointer',
        marginTop: -theme.spacing(0.5),
    },
    radioGroup: {
        padding: '18px 0 18px 15px',
    },
    radioButton: {
        color: '#989898',
        padding: 0,
        '&.Mui-checked': { color: '#1899D4' },
    },
    radioLabel: {
        '& .MuiFormControlLabel-label': {
            marginLeft: '0px',
            marginRight: '0px',
            fontSize: '13px',
        },
    },
    manageOptionsLabel: {
        fontSize: '20px',
    },
    cancelButton: {
        color: '#1899D4',
        fontSize: '18px',
        fontWeight: 500,
        fontFamily: 'Roboto',
    },
    popoverContainer: {
        width: '200px',
        height: '197px',
        border: '1px solid #C9C9C9',
        borderRadius: '8px',
        boxShadow: '0px 3px 15px 0px #00000059',
    },
    gridContainer: {
        padding: '15px 12px 15px 12px',
    },
}))
const OptionSelection = memo(() => {
    const classes = useStyles()

    const { setManageOptionSelected } = useHoveredItemActions()

    const handleResetOption = useCallback(
        (event) => {
            const value = event.target?.value?.toUpperCase()
            setManageOptionSelected(value)
        },
        [setManageOptionSelected]
    )

    return (
        <RadioGroup className={classes.radioGroup} onChange={handleResetOption}>
            <FormControlLabel
                control={<Radio className={classes.radioButton} size="small" />}
                value={'reset'}
                label="Reset All Options"
                labelPlacement="end"
                className={classes.radioLabel}
            />
            <FormControlLabel
                value={'deactivate'}
                control={<Radio className={classes.radioButton} size="small" />}
                label="Deactivate All Options"
                labelPlacement="end"
                className={classes.radioLabel}
            />
        </RadioGroup>
    )
})

const ActionButtons = memo<{
    onClose: (event: React.MouseEvent<HTMLButtonElement>) => void
    onSave: (event: React.MouseEvent<HTMLButtonElement>) => void
}>(({ onClose, onSave }) => {
    const classes = useStyles()

    return (
        <GridContainer direction="row">
            <GridItem xs={6}>
                <Button
                    id="cancelButton"
                    data-testid="cancelButton"
                    type="button"
                    aria-label="Cancel"
                    role="button"
                    variant="text"
                    className={classes.cancelButton}
                    onClick={onClose}
                >
                    Cancel
                </Button>
            </GridItem>
            <GridItem xs={6}>
                <Button
                    id="saveButton"
                    data-testid="saveButton"
                    type="button"
                    aria-label="Save"
                    role="button"
                    variant="text"
                    className={classes.cancelButton}
                    onClick={onSave}
                >
                    Save
                </Button>
            </GridItem>
        </GridContainer>
    )
})

export const ManageOptionsPopover: React.FC<ManageOptionsPopoverProps> = ({
    anchorElement,
    isOpen,
    onClose,
    onSave,
}) => {
    const classes = useStyles()

    return (
        <Popover
            open={isOpen}
            anchorEl={anchorElement}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'center',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'center',
                horizontal: 'left',
            }}
            classes={{ paper: classes.popoverContainer }}
        >
            <GridContainer direction="row" className={classes.gridContainer}>
                <GridItem>
                    <Typography className={classes.manageOptionsLabel}>
                        Manage Options
                    </Typography>
                </GridItem>
                <GridItem>
                    <OptionSelection />
                </GridItem>
                <ActionButtons onClose={onClose} onSave={onSave} />
            </GridContainer>
        </Popover>
    )
}
